'use client'

import { useMandatoryContext } from '@repo/hooks'
import {
  createContext,
  memo,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'

import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider'

export interface UpdatesInProgressContextProps {
  isUpdatesInProgressDialogOpen: boolean
  signalUpdatesInProgress: () => void
  closeUpdatesInProgressDialog: () => void
}

export const UpdatesInProgressContext = createContext<UpdatesInProgressContextProps | null>(null)

export const useUpdatesInProgress = () => {
  return useMandatoryContext(UpdatesInProgressContext, 'UpdatesInProgressContext')
}

export type UpdatesInProgressContextProviderProps = PropsWithChildren

const UpdatesInProgressContextProvider = ({ children }: UpdatesInProgressContextProviderProps) => {
  const [isUpdatesInProgressDialogOpen, setIsUpdatesInProgressDialogOpen] = useState(false)
  const [hasUpdatesInProgressDialogBeenOpened, setHasUpdatesInProgressDialogBeenOpened] =
    useState(false)
  const { updatesInProgress } = useFeatureFlags()

  const signalUpdatesInProgress = useCallback(() => {
    if (!hasUpdatesInProgressDialogBeenOpened) {
      setIsUpdatesInProgressDialogOpen(true)
      setHasUpdatesInProgressDialogBeenOpened(true)
    }
  }, [hasUpdatesInProgressDialogBeenOpened])

  useEffect(() => {
    if (updatesInProgress) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      signalUpdatesInProgress()
    }
  }, [signalUpdatesInProgress, updatesInProgress])

  const value = useMemo(
    () => ({
      isUpdatesInProgressDialogOpen,
      signalUpdatesInProgress,
      closeUpdatesInProgressDialog: () => setIsUpdatesInProgressDialogOpen(false)
    }),
    [signalUpdatesInProgress, isUpdatesInProgressDialogOpen]
  )

  return (
    <UpdatesInProgressContext.Provider value={value}>{children}</UpdatesInProgressContext.Provider>
  )
}

export default memo(UpdatesInProgressContextProvider)
